// pages/AdminUsers.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import api from '../services/api';

const AdminUsers = ({keywordsData, language}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Custom confirmation states
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({});
  const [actionCallback, setActionCallback] = useState(null);

  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    wallet_id: '',
    points: 0,
    wallet_balance: 0,
    age: '',
    sex: '',
    user_phone: ''
  });

  const activeTxt = language === 'ar' ? 'نشط' : 'Active';
  const inActiveTxt = language === 'ar' ? 'غير نشط' : 'Inactive';
  const creatUserBtnTxt = language === 'ar' ? 'إنشاء' : 'Create';
  const updateUserBtnTxt = language === 'ar' ? 'تحديث' : 'Update';
  const creatUserTitleTxt = language === 'ar' ? 'إضافة مستخدم جديد' : 'Create new user';
  const updateUserTitleTxt = language === 'ar' ? 'تعديل المستخدم' : 'Update user';
  const verifiedTxt = language === 'ar' ? 'موثق' : 'Verified';
  const notVerifiedTxt = language === 'ar' ? 'غير موثق' : 'Not Verified';

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/adminusers');
      setUsers(response.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch users' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Custom confirmation function
  const showConfirm = (config, callback) => {
    setConfirmConfig(config);
    setActionCallback(() => callback);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    if (actionCallback) {
      actionCallback();
    }
    setActionCallback(null);
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setActionCallback(null);
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.wallet_id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.user_phone && user.user_phone.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Handle form input changes
  const handleInputChange = (field) => (e) => {
    setUserForm(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  // Open dialog for new user
  const handleNewUser = () => {
    setUserForm({
      username: '',
      email: '',
      password: '',
      role: 'user',
      wallet_id: '',
      points: 0,
      wallet_balance: 0,
      age: '',
      sex: '',
      user_phone: ''
    });
    setEditingUser(null);
    setDialogOpen(true);
  };

  // Open dialog for editing user
  const handleEditUser = (user) => {
    setUserForm({
      username: user.username,
      email: user.email,
      password: '', // Don't prefill password
      role: user.role,
      wallet_id: user.wallet_id,
      points: user.points,
      wallet_balance: user.walletBalance,
      age: user.age || '',
      sex: user.sex || '',
      user_phone: user.user_phone || ''
    });
    setEditingUser(user);
    setDialogOpen(true);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update existing user
        await api.put(`/adminusers/edit/${editingUser.id}`, userForm);
        setMessage({ type: 'success', text: 'User updated successfully!' });
      } else {
        // Create new user
        await api.post('/adminusers/add', userForm);
        setMessage({ type: 'success', text: 'User created successfully!' });
      }
      setDialogOpen(false);
      fetchUsers(); // Refresh the list
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Operation failed' 
      });
    }
  };

  // Handle delete user
  const handleDeleteUser = (user) => {
    showConfirm(
      {
        title: language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete',
        description: language === 'ar' ?`هل أنت متأكد من رغبتك في حذف المستخدم "${user.username}"؟ لا يمكن التراجع عن هذا الإجراء.` :`Are you sure you want to delete user "${user.username}"? This action cannot be undone.`,
        confirmText: language === 'ar' ? 'حذف' :'Delete',
        cancelText: language === 'ar' ? 'إلغاء' : 'Cancel'
      },
      async () => {
        try {
          await api.delete(`/adminusers/delete/${user.id}`);
          setMessage({ type: 'success', text: 'User deleted successfully!' });
          fetchUsers(); // Refresh the list
        } catch (error) {
          console.error('Delete error:', error);
          setMessage({ type: 'error', text: 'Failed to delete user' });
        }
      }
    );
  };

  // Direct verify phone - toggle verification status
  const handleTogglePhoneVerification = async (user) => {
    const newStatus = !user.is_phone_verified;
    
    showConfirm(
      {
        title: newStatus ? (language === 'ar' ? 'تأكيد التوثيق' : 'Confirm Verification') : (language === 'ar' ? 'إلغاء التوثيق' : 'Unverify Phone'),
        description: newStatus
          ? (language === 'ar' 
              ? `هل أنت متأكد من توثيق رقم الهاتف للمستخدم "${user.username}"؟`
              : `Are you sure you want to verify phone for user "${user.username}"?`)
          : (language === 'ar'
              ? `هل أنت متأكد من إلغاء توثيق رقم الهاتف للمستخدم "${user.username}"؟`
              : `Are you sure you want to unverify phone for user "${user.username}"?`),
        confirmText: language === 'ar' ? 'تأكيد' : 'Confirm',
        cancelText: language === 'ar' ? 'إلغاء' : 'Cancel'
      },
      async () => {
        try {
          await api.post(`/adminusers/toggle-phone-verification/${user.id}`);
          setMessage({ 
            type: 'success', 
            text: newStatus 
              ? (language === 'ar' ? 'تم توثيق رقم الهاتف بنجاح' : 'Phone verified successfully')
              : (language === 'ar' ? 'تم إلغاء توثيق رقم الهاتف' : 'Phone verification removed')
          });
          fetchUsers();
        } catch (error) {
          setMessage({ 
            type: 'error', 
            text: error.response?.data?.message || 'Failed to update phone verification' 
          });
        }
      }
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4, mb: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          {language === 'ar' ? 'إدارة المستخدمين' : 'Users Management'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewUser}
        >
          {language === 'ar' ? 'إضافة مستخدم' : 'Add new user'}
        </Button>
      </Box>

      {/* Message Alert */}
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      {/* Search and Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            placeholder={language === 'ar' ? 'ابحث عن المستخدمين حسب الاسم أو البريد الإلكتروني أو معرف المحفظة أو الهاتف...' : 'Search for users by name, email, wallet ID, or phone number...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {users.length} {language === 'ar' ? 'مستخدم' : 'User'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {users.filter(u => u.role === 'admin').length} {language === 'ar' ? 'مدير' : 'Admin'} •{' '}
                {users.filter(u => u.is_phone_verified).length} {language === 'ar' ? 'موثق' : 'Verified'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{language === 'ar' ? 'المستخدم' : 'User Name'}</TableCell>
              <TableCell>{language === 'ar' ? 'رقم المحفظة' : 'Wallet ID'}</TableCell>
              <TableCell>{language === 'ar' ? 'رتبة المستخدم' : 'Role'}</TableCell>
              <TableCell>{language === 'ar' ? 'رقم الهاتف' : 'Phone No'}</TableCell>
              <TableCell>{language === 'ar' ? 'توثيق الهاتف' : 'Phone Verified'}</TableCell>
              <TableCell>{language === 'ar' ? 'العمر/الجنس' : 'Age/Sex'}</TableCell>
              <TableCell>{language === 'ar' ? 'النقاط' : 'Points'}</TableCell>
              <TableCell>{language === 'ar' ? 'الرصيد' : 'Balance'}</TableCell>
              <TableCell>{language === 'ar' ? 'الحالة' : 'Status'}</TableCell>
              <TableCell align="center">{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2">{user.username}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={user.wallet_id} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.role}
                    color={user.role === 'admin' ? 'primary' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {user.user_phone || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {user.user_phone ? (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        icon={user.is_phone_verified ? <VerifiedIcon /> : <PhoneIcon />}
                        label={user.is_phone_verified ? verifiedTxt : notVerifiedTxt}
                        size="small"
                        sx={{
                          backgroundColor: user.is_phone_verified ? '#4CAF50' : '#FF9800',
                          color: '#FFFFFF',
                          fontWeight: 600
                        }}
                      />
                      <Tooltip title={user.is_phone_verified 
                        ? (language === 'ar' ? 'إلغاء التوثيق' : 'Unverify') 
                        : (language === 'ar' ? 'توثيق' : 'Verify')}>
                        <IconButton
                          size="small"
                          onClick={() => handleTogglePhoneVerification(user)}
                          sx={{ 
                            color: user.is_phone_verified ? '#FF9800' : '#4CAF50',
                            '&:hover': {
                              backgroundColor: user.is_phone_verified 
                                ? 'rgba(255, 152, 0, 0.1)' 
                                : 'rgba(76, 175, 80, 0.1)'
                            }
                          }}
                        >
                          {user.is_phone_verified ? <RefreshIcon /> : <CheckCircleIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No phone
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {user.age ? `${user.age} ${language === 'ar' ? 'سنة' : 'year'}` : 'N/A'} / {user.sex || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {user.points.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {user.walletBalance.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.is_active ? activeTxt : inActiveTxt}
                    color={user.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditUser(user)}
                      color="secondary"
                    >
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => handleDeleteUser(user)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" dir={language === 'ar' ? 'rtl' : 'ltr'} fullWidth>
        <DialogTitle>
          {editingUser ? updateUserTitleTxt: creatUserTitleTxt}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'إسم المستخدم' : 'User Name'}
                  value={userForm.username}
                  onChange={handleInputChange('username')}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'الإيميل' : 'E-mail'}
                  type="email"
                  value={userForm.email}
                  onChange={handleInputChange('email')}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'كلمة المرور' : 'Password'}
                  type="password"
                  value={userForm.password}
                  onChange={handleInputChange('password')}
                  required={!editingUser}
                  helperText={editingUser ? language === "ar" ? "اتركه فارغًا للاحتفاظ بكلمة المرور الحالية":"Leave it blank to save the current password." : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{language === 'ar' ? 'الرتبة' : 'Role'}</InputLabel>
                  <Select
                    value={userForm.role}
                    label={language === 'ar' ? 'الرتبة' : 'Role'}
                    onChange={handleInputChange('role')}
                    required
                  >
                    <MenuItem value="user">{language === 'ar' ? 'مستخدم' : 'User'}</MenuItem>
                    <MenuItem value="admin">{language === 'ar' ? 'مدير' : 'Admin'}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'رقم معرف المحفظة' : 'Wallet ID'}
                  value={userForm.wallet_id}
                  onChange={handleInputChange('wallet_id')}
                  placeholder={language === 'ar' ? 'رقم معرف المحفظة (6 أرقام)' : 'Wallet ID must be (6 numbers)'}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'العمر' : 'Age'}
                  type="number"
                  value={userForm.age}
                  onChange={handleInputChange('age')}
                  inputProps={{ min: 0, max: 120 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{language === 'ar' ? 'الجنس' : 'Sex'}</InputLabel>
                  <Select
                    value={userForm.sex}
                    label={language === 'ar' ? 'الجنس' : 'Sex'}
                    onChange={handleInputChange('sex')}
                  >
                    <MenuItem value="">{language === 'ar' ? 'إختيار' : 'Choose'}</MenuItem>
                    <MenuItem value="male">{language === 'ar' ? 'ذكر' : 'Male'}</MenuItem>
                    <MenuItem value="female">{language === 'ar' ? 'انثى' : 'Female'}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'رقم الهاتف' : 'Phone number'}
                  value={userForm.user_phone}
                  onChange={handleInputChange('user_phone')}
                  placeholder="e.g., +201234567890"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'النقاط' : 'Points'}
                  type="number"
                  value={userForm.points}
                  onChange={handleInputChange('points')}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'رصيد المحفظة' : 'Wallet Balance'}
                  type="number"
                  value={userForm.wallet_balance}
                  onChange={handleInputChange('wallet_balance')}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
            <Button type="submit" variant="contained">
              {editingUser ? updateUserBtnTxt : creatUserBtnTxt} {language === 'ar' ? 'المستخدم' : 'User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Custom Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={handleCancel} dir={language === 'ar' ? 'rtl' : 'ltr'} >
        <DialogTitle>{confirmConfig.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmConfig.description}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>
            {confirmConfig.cancelText || (language === 'ar' ? 'إلغاء' : 'Cancel')}
          </Button>
          <Button onClick={handleConfirm} color="error" variant="contained">
            {confirmConfig.confirmText || (language === 'ar' ? 'تأكيد' : 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminUsers;